import { Icon } from '@mui/material';
import React from 'react';
import classNames from 'classnames';
import { Text } from '../typography';
import styles from './ActionItemCard.module.scss';
import { dayDelta, fromNowOrToday, getLocalTime } from '../../utilities/time';
import LabelsDisplay from '../LabelsDisplay';
import {
  priorityStringLookup,
  statusStringLookup,
} from './action_item_utilities';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useIsMobile } from '../../hooks/misc';
import SimpleUserCard from '../SimpleUserCard';
import MultiAvatar from '../MultiAvatar';
import { customColors } from '../../utilities';
import TextWithIcon from '../typography/TextWithIcon';

export default function ActionItemCard({
  actionItem,
  className,
  onClick,
  maxLabels,
  disabled = false,
  showName = false,
}) {
  const mobile = useIsMobile();
  const { t } = useTranslation();
  const { labels, status, priority } = actionItem || {};
  const allLabels = [
    {
      color: statusStringLookup[status]?.color || 'primary',
      name: t(statusStringLookup[status]?.title) || status,
    },
    {
      color: priorityStringLookup[priority]?.color || 'primary',
      name:
        `${t(priorityStringLookup[priority]?.name)} ${t(
          keys.action_items.PRIORITY,
        )}` || status,
    },
    ...(labels || []),
  ];
  const days = dayDelta(actionItem.dueDate);
  function dueDateColor() {
    if (!actionItem?.dueDate) return undefined;
    if (days < 1) {
      return customColors.RED;
    }
    if (days <= 7) {
      return customColors.YELLOW;
    }
    return customColors.GREEN;
  }
  const backgroundColor = dueDateColor();
  const isComplete = actionItem?.status === 'DONE';

  return (
    <div
      className={classNames(
        styles.container,
        className,
        disabled ? styles.disabled : null,
      )}
      onClick={onClick}
    >
      <div>
        <img
          className={styles.leftAvatar}
          src={actionItem.creator?.avatarUrl}
          alt="creator"
        />
      </div>
      <div className={styles.middle}>
        <div className={styles.header}>
          <TextWithIcon
            iconProps={{ color: customColors.RED }}
            leftIcon={
              days < 1 && !isComplete && actionItem?.dueDate
                ? 'error_outlined'
                : ''
            }
            noMargin
            weight="semibold"
            truncate
          >
            {actionItem.title}
          </TextWithIcon>
          {actionItem?.assignee?.id ? (
            mobile || !showName ? (
              <img
                alt="user avatar"
                className={styles.avatar}
                src={actionItem.assignee.avatarUrl}
              />
            ) : (
              <div className={styles.alignRight}>
                <SimpleUserCard
                  mediumAvatar
                  user={actionItem.assignee}
                  size="sm"
                />
              </div>
            )
          ) : (
            <div />
          )}
        </div>
        <div>
          <Text noMargin size="sm" truncate>
            {`${getLocalTime(actionItem.dateCreated).fromNow()}${
              actionItem.description ? ` - ${actionItem.description}` : ''
            }`}
          </Text>
        </div>
        <div style={{ height: '0.2rem' }} />
        <div className={styles.header}>
          <div className={styles.flex}>
            {actionItem.dueDate && actionItem.status !== 'DONE' ? (
              <div className={styles.dateLabel} style={{ backgroundColor }}>
                <Icon
                  baseClassName="material-icons-outlined"
                  sx={{ fontSize: '0.75rem', color: 'white' }}
                >
                  timer
                </Icon>
                <Text weight="semiBold" color="white" noMargin size="xs">
                  {fromNowOrToday(actionItem.dueDate)}
                </Text>
              </div>
            ) : null}
            <LabelsDisplay
              labels={allLabels}
              maxLabels={maxLabels || mobile ? 2 : 4}
            />
          </div>
          <MultiAvatar users={actionItem.participants ?? []} size="xs" />
        </div>
      </div>
    </div>
  );
}
